import { useMemo, useCallback, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Tooltip } from "@/shared/ui/tooltip";
import { X, Download, Loader2, FileWarning } from "lucide-react";
import type { FileItem } from "../../types";
import { isPdf } from "../../utils/fileUtils";
import { useImageZoom } from "../../hooks/image-dialog/useImageZoom";
import { useImagePan } from "../../hooks/image-dialog/useImagePan";
import { useImageLoader } from "../../hooks/image-dialog/useImageLoader";
import { useImageFullscreen } from "../../hooks/image-dialog/useImageFullscreen";
import { useImageKeyboard } from "../../hooks/image-dialog/useImageKeyboard";
import { ImageDialogControls } from "./ImageDialogControls";
import { ImageDialogNavigation } from "./ImageDialogNavigation";
import { ImageDialogViewer } from "./ImageDialogViewer";
import { useFileDownload } from "../../hooks/useFileDownload";

function isViewableFile(file: FileItem): boolean {
  return file.type === "image" || isPdf(file);
}

interface ImageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file: FileItem;
  files: FileItem[];
  onNavigate?: (direction: "prev" | "next") => void;
}

export function ImageDialog({
  open,
  onOpenChange,
  file,
  files,
  onNavigate,
}: ImageDialogProps) {
  const isFilePdf = isPdf(file);

  const viewableFiles = useMemo(() => files.filter(isViewableFile), [files]);
  const currentIndex = useMemo(
    () => viewableFiles.findIndex((f) => f.id === file.id),
    [viewableFiles, file.id],
  );
  const canNavigatePrev = currentIndex > 0;
  const canNavigateNext = currentIndex < viewableFiles.length - 1;

  const { imageUrl, isLoading, imageError } = useImageLoader({
    fileId: file.id,
    open,
  });

  const { zoom, handleZoomIn, handleZoomOut, handleResetZoom } = useImageZoom({
    open,
    fileId: file.id,
  });

  const { pan, isDragging, imageContainerRef, handleMouseDown, resetPan } =
    useImagePan({
      zoom,
      open,
      fileId: file.id,
    });

  const handleResetZoomWithPan = useCallback(() => {
    handleResetZoom();
    resetPan();
  }, [handleResetZoom, resetPan]);

  const { isFullscreen, handleFullscreen } = useImageFullscreen({ open });
  const { isDownloading, downloadFile } = useFileDownload({ file });

  const [confidenceFilter, setConfidenceFilter] = useState<number | null>(null);

  const hasClassifications =
    file.classifications && file.classifications.length > 0;

  const handleNavigate = useCallback(
    (direction: "prev" | "next") => {
      if (!onNavigate) return;

      if (direction === "prev" && canNavigatePrev) {
        onNavigate("prev");
      } else if (direction === "next" && canNavigateNext) {
        onNavigate("next");
      }
    },
    [onNavigate, canNavigatePrev, canNavigateNext],
  );

  useImageKeyboard({
    open: open && !isFilePdf,
    onClose: () => onOpenChange(false),
    canNavigatePrev,
    canNavigateNext,
    onNavigate: handleNavigate,
    onZoomIn: handleZoomIn,
    onZoomOut: handleZoomOut,
    onResetZoom: handleResetZoomWithPan,
    onFullscreen: handleFullscreen,
  });

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none p-0 bg-black/95 border-0 overflow-hidden">
        <DialogTitle className="sr-only">
          {isFilePdf ? "PDF" : "Image"} Viewer - {file.name}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {isFilePdf
            ? "Full-screen PDF viewer. Use arrow keys to navigate between files."
            : "Full-screen image viewer. Use arrow keys to navigate, mouse wheel to zoom, and double-click to reset zoom."}
        </DialogDescription>
        <div className="relative flex items-center justify-center w-full h-full min-w-0 min-h-0">
          {isFilePdf ? (
            <>
              <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
                <Tooltip content="Download file">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="bg-black/50 hover:bg-white/70 text-white border border-white/20"
                    onClick={downloadFile}
                    disabled={isDownloading}
                    aria-label="Download PDF"
                  >
                    <Download className="h-5 w-5" />
                  </Button>
                </Tooltip>
                <Button
                  variant="ghost"
                  size="icon"
                  className="bg-black/50 hover:bg-white/70 text-white border border-white/20"
                  onClick={() => onOpenChange(false)}
                  aria-label="Close viewer"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <ImageDialogNavigation
                canNavigatePrev={canNavigatePrev}
                canNavigateNext={canNavigateNext}
                onNavigate={handleNavigate}
              />

              <div className="flex-1 flex items-center justify-center w-full h-full p-12 pt-16">
                {isLoading && (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-10 w-10 text-white animate-spin" />
                    <span className="text-white/70 text-sm">
                      Loading PDF...
                    </span>
                  </div>
                )}

                {imageError && !isLoading && (
                  <div className="flex flex-col items-center gap-3">
                    <FileWarning className="h-12 w-12 text-white/50" />
                    <span className="text-white/70 text-sm">
                      Failed to load PDF
                    </span>
                    <Button
                      variant="ghost"
                      className="text-white border border-white/20 hover:bg-white/10"
                      onClick={downloadFile}
                      disabled={isDownloading}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download instead
                    </Button>
                  </div>
                )}

                {imageUrl && !isLoading && !imageError && (
                  <iframe
                    src={imageUrl}
                    title={`${file.name}${file.extension}`}
                    className="w-full h-full rounded-lg bg-white"
                  />
                )}
              </div>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 z-50 bg-black/50 hover:bg-white/70 text-white border border-white/20"
                onClick={() => onOpenChange(false)}
                aria-label="Close image"
              >
                <X className="h-5 w-5" />
              </Button>

              <ImageDialogNavigation
                canNavigatePrev={canNavigatePrev}
                canNavigateNext={canNavigateNext}
                onNavigate={handleNavigate}
              />

              <ImageDialogViewer
                file={file}
                imageUrl={imageUrl}
                isLoading={isLoading}
                imageError={imageError}
                zoom={zoom}
                pan={pan}
                isDragging={isDragging}
                imageContainerRef={imageContainerRef}
                onMouseDown={handleMouseDown}
                onResetZoom={handleResetZoomWithPan}
                minConfidence={confidenceFilter}
                largeThumbnailDimensions={file.largeThumbnail}
              />

              <ImageDialogControls
                zoom={zoom}
                isFullscreen={isFullscreen}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                onFullscreen={handleFullscreen}
                onDownload={downloadFile}
                isDownloading={isDownloading}
                hasClassifications={hasClassifications}
                confidenceFilter={confidenceFilter}
                onConfidenceFilterChange={setConfidenceFilter}
              />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
